import React, { FC, useMemo, useState } from 'react';
import type { DataExtensionSchema } from '@wix/ambassador-data-extensions-v1-data-extension-schema/types';
import { useWidgetActions } from '../hooks/useWidgetActions';
import { useWidgetViewModel } from '../hooks/useWidgetViewModel';
import loadable from '@wix/yoshi-flow-editor/loadable';
import { FilterButton } from '../FilterButton/FilterButton';
import type { FilterFieldDescriptor } from './types';

const FilterPanel = loadable(
  () =>
    import(
      /* webpackChunkName: "FilterPanel" */ '../FilterPanel/FilterPanel'
    ),
);

function extractFilterFields(
  schemas: DataExtensionSchema[] | undefined,
): FilterFieldDescriptor[] {
  if (!schemas?.length) {
    return [];
  }

  const fields: FilterFieldDescriptor[] = [];

  for (const schema of schemas) {
    const properties = schema.jsonSchema?.properties;
    if (!properties) {
      continue;
    }

    const namespace = schema.namespace ?? '';

    for (const [key, def] of Object.entries<any>(properties)) {
      const label = def?.title || key;
      const rawType: string = def?.type ?? 'string';
      const format: string | undefined = def?.format;

      let type: FilterFieldDescriptor['type'];
      if (rawType === 'array') {
        type = 'array';
      } else if (rawType === 'boolean') {
        type = 'boolean';
      } else if (rawType === 'integer') {
        type = 'integer';
      } else if (rawType === 'number') {
        type = 'number';
      } else if (rawType === 'string' && format === 'date-time') {
        type = 'date-time';
      } else if (rawType === 'string' && format === 'date') {
        type = 'date';
      } else if (rawType === 'string' && format === 'multi-line') {
        type = 'long-text';
      } else {
        type = 'string';
      }

      const enumValues: string[] | undefined =
        Array.isArray(def?.enum) ? def.enum
        : Array.isArray(def?.items?.enum) ? def.items.enum
        : undefined;
      fields.push({ key, namespace, label, type, enumValues });
    }
  }

  return fields;
}

export const Filters: FC = () => {
  const { dataExtensionSchemas } = useWidgetViewModel();
  const { searchServices, clearSearchServices } = useWidgetActions();
  const [isOpen, setIsOpen] = useState(false);

  const filterFields = useMemo(
    () => extractFilterFields(dataExtensionSchemas),
    [dataExtensionSchemas],
  );

  if (!filterFields.length) {
    return null;
  }

  return (
    <>
      <FilterButton onClick={() => setIsOpen(true)} />
      <FilterPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        filterFields={filterFields}
        onSearch={(filterValues: Record<string, any>) => searchServices(filterValues, filterFields)}
        onClear={clearSearchServices}
      />
    </>
  );
};
